import { jsx as _jsx } from "react/jsx-runtime";
import { Home } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useProjectVolumes } from '../../hooks/useProjectVolumes';
import { useProposalProject } from '../../hooks/useProposalProject';
import { proposalHref } from '../../lib/proposalHref';
import { PRICING_NODE_LABELS } from '../ProposalDashboard/v2/pricing/pricingRoutes';
import { flattenSections, useOutlineSections } from '../ProposalDashboard/v2/volume/workingOutlineHooks';
import { PROPOSAL_SECTION_LABELS } from './proposalNav';
import { useProposalPursuit } from './useProposalPursuit';
import { useRecordBreadcrumbs } from './useRecordBreadcrumbs';
function titleCase(s) {
    return s.split('-').filter(Boolean).map((w)=>w[0]?.toUpperCase() + w.slice(1)).join(' ');
}
/** Topbar trail for a proposal under the record shell (FND-20): Home / Growth / <name> / <section>. */ export function ProposalRecordBreadcrumbs({ projectId }) {
    const location = useLocation();
    const { data } = useProposalProject(projectId);
    const volumes = useProjectVolumes(projectId);
    const pursuit = useProposalPursuit(projectId);
    const rest = location.pathname.slice(`/proposal/${projectId}`.length).split('/').filter(Boolean);
    const sectionId = rest[0] === 'v2' && rest[1] === 'volume' && rest[3] === 'section' ? rest[4] : '';
    // WRT-17: /volume/:volumeId/section/:sectionId ends "… / {volume} / §n title".
    const outline = useOutlineSections(projectId, rest[2], !!sectionId);
    let label = 'Home';
    let volumeCrumb;
    if (rest[0] === 'v2') {
        const key = rest[1] ?? 'home';
        if (key === 'volume') {
            label = volumes.data?.find((v)=>v.volumeId === rest[2])?.displayName ?? 'Volume';
            if (sectionId) {
                const live = flattenSections(outline.data ?? []).filter((s)=>!s.orphanedAt).sort((a, b)=>a.sortOrder - b.sortOrder);
                const target = live.find((s)=>s.sectionId === sectionId);
                const sectionDoc = target?.sourceDocumentId;
                const inDoc = live.filter((s)=>s.sourceDocumentId === sectionDoc);
                volumeCrumb = {
                    label,
                    href: proposalHref(projectId, `volume/${rest[2]}`)
                };
                label = target ? `§${inDoc.indexOf(target) + 1} ${target.title}` : 'Section';
            }
        } else if (key === 'sources') {
            const sub = rest[2] ?? '';
            label = PROPOSAL_SECTION_LABELS[sub] ?? titleCase(sub);
            if (sub === 'pricing' && rest[3]) {
                volumeCrumb = {
                    label: 'Pricing',
                    href: proposalHref(projectId, 'sources/pricing')
                };
                label = rest[3] === 'scenarios' ? 'Scenarios' : rest[4] === 'clin' ? `CLIN ${rest[5]}` : PRICING_NODE_LABELS[rest[4] ?? 'rates'] ?? 'Rate card';
            }
        } else {
            label = PROPOSAL_SECTION_LABELS[key] ?? titleCase(key);
        }
    } else if (rest[0] === 'settings') {
        label = 'Settings';
    }
    const name = data?.project.name;
    useRecordBreadcrumbs(name ? [
        {
            label: 'Home',
            href: '/',
            icon: /*#__PURE__*/ _jsx(Home, {
                className: "h-3 w-3"
            })
        },
        {
            label: 'Growth',
            href: '/growth'
        },
        ...pursuit ? [
            {
                label: pursuit.name,
                href: pursuit.href
            }
        ] : [],
        {
            label: name,
            href: proposalHref(projectId)
        },
        ...volumeCrumb ? [
            volumeCrumb
        ] : [],
        {
            label
        }
    ] : null);
    return null;
}
